import { useQuery } from "@tanstack/react-query";
import { type CurrentUserResponse, getCurrentUserInfoOptions } from "@/api";
import { useAuth } from "@/auth/AuthContext";

/**
 * The signed-in user's backend profile (GET /auth/me): user, team memberships,
 * budget, platform capabilities. The single source for "who am I" in web/.
 */
export function useCurrentUser() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    ...getCurrentUserInfoOptions(),
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
    select: (res) => res.data,
  });
}

export interface Permissions {
  /** platform_admin or install_admin: admin console, sees all resources. */
  isPlatformAdmin: boolean;
  /** install_admin only: license + platform suspension. */
  isInstallAdmin: boolean;
}

const NO_PERMISSIONS: Permissions = { isPlatformAdmin: false, isInstallAdmin: false };

/**
 * UI-level permission flags derived from the profile. These only decide what
 * to SHOW; the backend enforces every permission independently.
 */
export function permissionsFor(me: CurrentUserResponse | undefined): Permissions {
  if (!me) return NO_PERMISSIONS;
  const role = me.user.role;
  return {
    isPlatformAdmin: role === "platform_admin" || role === "install_admin",
    isInstallAdmin: role === "install_admin",
  };
}

export function usePermissions(): Permissions {
  return permissionsFor(useCurrentUser().data);
}

export type Capabilities = Required<CurrentUserResponse["capabilities"]>;

const NO_CAPABILITIES: Capabilities = {
  automatedSyncEnabled: false,
  oauthClientManagementEnabled: false,
  sharepointEnabled: false,
  googleDriveEnabled: false,
  fabricEnabled: false,
  azureAdminEnabled: false,
  knowledgeGraphEnabled: false,
  agentMemoryEnabled: false,
  knowledgeExtractionEnabled: false,
  rallyEnabled: false,
  chatFilePipelineEnabled: false,
  transcriptionEnabled: false,
  researchEnabled: false,
};

/**
 * What this deployment supports (SharePoint, Google Drive, Fabric...), from
 * the backend. Gate integration UI on these, NEVER on the configured auth
 * provider: the backend owns that mapping (Settings.*_available).
 */
export function useCapabilities(): Capabilities {
  const caps = useCurrentUser().data?.capabilities;
  return { ...NO_CAPABILITIES, ...caps };
}
